import { useRef, useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query-v4';

/**
 * Hook to create a task-scoped QueryClient instance.
 * This ensures a fresh QueryClient state for each task, preventing cache pollution between tasks.
 *
 * @param taskId - the current task ID. When this changes, a new QueryClient is created.
 * @returns stable QueryClient instance for the current task.
 */
export const useTaskQueryClientV4 = (taskId: string | undefined): QueryClient => {
  // use a ref to store the QueryClient instance, necessary to avoid re-creating it on every render
  const queryClient = useRef(new QueryClient());
  const previousTaskId = useRef(taskId);

  // if taskId changes, create a new QueryClient
  useEffect(() => {
    if (previousTaskId.current !== taskId) {
      queryClient.current = new QueryClient();
      previousTaskId.current = taskId;
    }
  }, [taskId]);

  return queryClient.current;
};
